/**
 * 거래처 상세 보기 (read-only) — 재무 표 등에서 거래처명 클릭 시 호출.
 *
 * 재무팀이 관할하는 거래처 마스터의 모든 항목을 펼쳐 보여준다 (결재 상세 보기와 같은 컨셉).
 * 편집은 거래처 관리 탭에서. 여기선 보기 + 첨부 다운로드만.
 */
import React, { useEffect, useState } from "react";
import { Box, Chip, Divider, IconButton, Stack, Typography } from "@mui/material";
import OpenInNewRoundedIcon   from "@mui/icons-material/OpenInNewRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";

import { financeApi } from "../../api/financeApi";
import { formatPhone } from "../../../../util/phone";
import DetailModal from "../../../../component/common/DetailModal";
import LoadingDots from "../../../../component/common/LoadingDots";

const TYPE_LABEL = { GENERAL: "일반 사업자", PUBLIC: "공공기관" };

export default function VendorDetailDialog({ open, vendorId, onClose }) {
    const [vendor, setVendor]           = useState(null);
    const [attachments, setAttachments] = useState([]);
    const [loading, setLoading]         = useState(true);

    useEffect(() => {
        if (!open || !vendorId) return;
        setLoading(true);
        Promise.all([
            financeApi.getVendor(vendorId).catch(() => null),
            financeApi.listVendorAttachments(vendorId).catch(() => []),
        ]).then(([v, atts]) => {
            setVendor(v?.data ?? v ?? null);
            setAttachments(atts?.data ?? atts ?? []);
        }).finally(() => setLoading(false));
    }, [open, vendorId]);

    return (
        <DetailModal
            open={open}
            onClose={onClose}
            title={vendor?.name || "거래처 상세"}
            subtitle={vendor ? (TYPE_LABEL[vendor.type] || vendor.type) : undefined}
            maxWidth="md"
        >
            {loading || !vendor ? (
                <Box sx={{ p: 4, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                    {loading ? <LoadingDots/> : "거래처 정보를 찾을 수 없습니다."}
                </Box>
            ) : (
                <>
                    <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 2 }}>
                        <Chip size="small" label={TYPE_LABEL[vendor.type] || vendor.type}
                            sx={{
                                height: 22, fontSize: 11, fontWeight: 700,
                                bgcolor: vendor.type === "PUBLIC" ? "rgba(22,163,74,0.10)" : "rgba(37,99,235,0.10)",
                                color:   vendor.type === "PUBLIC" ? "#15803d"             : "#1d4ed8",
                            }} />
                    </Stack>

                    <Section title="기본 정보">
                            <KvGrid items={[
                                ["사업자등록번호", vendor.bizRegistrationNo],
                                ["대표자", vendor.representative],
                                ["업태", vendor.bizType],
                                ["종목", vendor.bizItem],
                                ["주소", vendor.address],
                            ]} />
                        </Section>

                        <Section title="연락처">
                            <KvGrid items={[
                                ["전화번호",       vendor.phone ? formatPhone(vendor.phone) : null],
                                ["이메일",         vendor.email],
                                ["담당자",         vendor.contactPerson],
                                ["담당자 연락처",  vendor.contactPhone ? formatPhone(vendor.contactPhone) : null],
                            ]} />
                        </Section>

                        <Section title="입금 계좌">
                            <KvGrid items={[
                                ["은행",     vendor.bankName],
                                ["계좌번호", vendor.bankAccountNo],
                                ["예금주",   vendor.bankAccountHolder],
                            ]} />
                        </Section>

                        {/* 사업자등록증 / 통장사본 미리보기 */}
                        {(vendor.bizLicenseImageUrl || vendor.bankbookImageUrl) && (
                            <Section title="등록 이미지">
                                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                                    {vendor.bizLicenseImageUrl && (
                                        <ImagePreview
                                            label={vendor.type === "PUBLIC" ? "기관 증명서" : "사업자등록증"}
                                            url={vendor.bizLicenseImageUrl} />
                                    )}
                                    {vendor.bankbookImageUrl && (
                                        <ImagePreview label="통장 사본" url={vendor.bankbookImageUrl} />
                                    )}
                                </Stack>
                            </Section>
                        )}

                        {attachments.length > 0 && (
                            <Section title={`추가 첨부 (${attachments.length})`}>
                                <Stack spacing={0.8}>
                                    {attachments.map(a => (
                                        <Stack key={a.id} direction="row" alignItems="center" spacing={1}
                                            sx={{
                                                p: 1, borderRadius: 1, border: "1px solid #eef2f6",
                                                bgcolor: "#fafbfc",
                                            }}>
                                            {a.image && a.url ? (
                                                <Box component="img" src={a.url} alt=""
                                                    sx={{ width: 36, height: 36, borderRadius: 0.6, objectFit: "cover", flexShrink: 0 }} />
                                            ) : (
                                                <Box sx={{
                                                    width: 36, height: 36, borderRadius: 0.6,
                                                    bgcolor: "#eef2f6", color: "#64748b",
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                    flexShrink: 0,
                                                }}>
                                                    <InsertDriveFileRoundedIcon sx={{ fontSize: 20 }} />
                                                </Box>
                                            )}
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box sx={{
                                                    fontSize: 12.5, fontWeight: 700, color: "#0f172a",
                                                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                }}>{a.filename}</Box>
                                                <Box sx={{ fontSize: 10.5, color: "#94a3b8" }}>
                                                    {(a.sizeBytes != null ? Math.round(a.sizeBytes / 1024) : 0)} KB
                                                </Box>
                                            </Box>
                                            <IconButton size="small" component="a" href={a.url} target="_blank" rel="noopener noreferrer">
                                                <OpenInNewRoundedIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    ))}
                                </Stack>
                            </Section>
                        )}

                    {vendor.memo && (
                        <Section title="메모">
                            <Box sx={{
                                p: 1.5, borderRadius: 1.2, bgcolor: "#f8fafc",
                                fontSize: 13, color: "#334155", whiteSpace: "pre-wrap",
                            }}>
                                {vendor.memo}
                            </Box>
                        </Section>
                    )}
                </>
            )}
        </DetailModal>
    );
}

/* ─── 헬퍼 ────────────────────────────────────────────── */
function Section({ title, children }) {
    return (
        <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#0f172a", mb: 0.7 }}>
                {title}
            </Typography>
            <Divider sx={{ mb: 1.2, borderColor: "#eef2f6" }} />
            {children}
        </Box>
    );
}

function KvGrid({ items }) {
    return (
        <Box sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 1,
        }}>
            {items.map(([k, v]) => (
                <Stack key={k} direction="row" spacing={1} sx={{ py: 0.5 }}>
                    <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#94a3b8", minWidth: 96 }}>
                        {k}
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a", flex: 1, wordBreak: "break-all" }}>
                        {v || "—"}
                    </Typography>
                </Stack>
            ))}
        </Box>
    );
}

function ImagePreview({ label, url }) {
    return (
        <Box sx={{ flex: 1, border: "1px solid #eef2f6", borderRadius: 1.2, p: 1 }}>
            <Stack direction="row" alignItems="center" sx={{ mb: 0.6 }}>
                <Typography sx={{ flex: 1, fontSize: 11.5, fontWeight: 700, color: "#475569" }}>
                    {label}
                </Typography>
                <IconButton size="small" component="a" href={url} target="_blank" rel="noopener noreferrer">
                    <OpenInNewRoundedIcon fontSize="small" />
                </IconButton>
            </Stack>
            <Box sx={{
                height: 140, borderRadius: 0.8, overflow: "hidden",
                bgcolor: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center",
            }}>
                <Box component="img" src={url} alt={label}
                    sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
            </Box>
        </Box>
    );
}
